import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import api, { getPublishers, getOrganizations, getDocumentTypes, getCourtTypes } from '../services/api';
import { DocumentPreview } from '../components/DocumentPreview';
import AutoComplete from '../components/AutoComplete';

interface UploadForm {
  title: string;
  document_type: string;
  author?: string;
  summary?: string;
  citation?: string;
  publisher?: string;
  organization?: string;
  court?: string;
  date_of_document?: string;
}

export interface DocumentType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface CourtType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

const DocumentUpload: React.FC = () => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [useOCR, setUseOCR] = useState(false);
  const [publishers, setPublishers] = useState<string[]>([]);
  const [publishersLoading, setPublishersLoading] = useState(false);
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [documentTypesLoading, setDocumentTypesLoading] = useState(false);
  const [courtTypes, setCourtTypes] = useState<CourtType[]>([]);
  const [courtTypesLoading, setCourtTypesLoading] = useState(false);
  const [topics, setTopics] = useState<string[]>([]);
  const [currentTopic, setCurrentTopic] = useState('');

  const addTopic = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.preventDefault();
    const trimmed = currentTopic.trim();
    if (trimmed && !topics.includes(trimmed)) {
      setTopics([...topics, trimmed]);
      setCurrentTopic('');
    }
  };

  const removeTopic = (topicToRemove: string) => {
    setTopics(topics.filter(t => t !== topicToRemove));
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    trigger
  } = useForm<UploadForm>(
    {
      mode: 'onChange', // Validate on change for better UX
      defaultValues: {
        title: '',
        document_type: '',
        summary: '',
        citation: '',
        publisher: '',
        organization: '',
        court: '',
        date_of_document: '',
      },
    }
  );

  const previewRef = useRef<HTMLDivElement>(null);

  const publisherValue = watch('publisher', '');
  const organizationValue = watch('organization', '');


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
    },
    onDrop: (acceptedFiles) => {
      setUploadedFiles(acceptedFiles);
      setTimeout(() => {
        previewRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    },

  });

  // const documentTypes = [
  //   'Judgement',
  //   'Article',
  //   'Commentary',
  //   'Case Study',
  //   'Legal Opinion',
  //   'Contract',
  //   'Statute',
  //   'Regulation',
  //   'Other',
  // ];

  const onSubmit = async (data: UploadForm) => {
    if (uploadedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    if (isUploading) {
      toast.error('Upload already in progress');
      return;
    }

    console.log('Starting upload process...');
    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const formData = new FormData();

        formData.append('file', file);
        formData.append('title', data.title || file.name);
        formData.append('document_type', data.document_type);

        if (data.author) formData.append('author', data.author);
        if (topics.length > 0) formData.append('topics', JSON.stringify(topics));

        if (data.summary) formData.append('summary', data.summary);
        if (data.citation) formData.append('citation', data.citation);
        if (data.publisher) formData.append('publisher', data.publisher);
        if (data.organization) formData.append('organization', data.organization);
        if (data.court) formData.append('court', data.court);
        if (data.date_of_document) formData.append('date_of_document', data.date_of_document);

        // Add OCR parameter
        formData.append('use_ocr', useOCR.toString());

        // Add timeout to prevent hanging
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout

        try {
          console.log(`Uploading file ${i + 1}/${uploadedFiles.length}: ${file.name}`);
          const response = await api.post('/api/documents/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            signal: controller.signal,
            timeout: 300000, // 5 minute timeout
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(progress);
                console.log(`Upload progress for ${file.name}: ${progress}%`);
              }
            },
          });

          clearTimeout(timeoutId);
          const result = response.data;
          console.log('Upload result:', result);

          setUploadProgress(((i + 1) / uploadedFiles.length) * 100);
        } catch (uploadError: any) {
          clearTimeout(timeoutId);
          console.error(`Upload error for ${file.name}:`, uploadError);
          if (uploadError.name === 'AbortError') {
            throw new Error('Upload timeout - please try again');
          }
          throw uploadError;
        }
      }

      toast.success('Documents uploaded successfully!');
      setUploadedFiles([]);
      setTopics([]);
      reset();
      setUploadProgress(0);
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload documents. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
  };

  // Load publishers and organizations on component mount
  useEffect(() => {
    const loadData = async () => {
      // Load publishers
      setPublishersLoading(true);
      try {
        const publishersData = await getPublishers();
        setPublishers(publishersData);
      } catch (error) {
        console.error('Failed to load publishers:', error);
        toast.error('Failed to load publishers');
      } finally {
        setPublishersLoading(false);
      }

      // Load document types

      setDocumentTypesLoading(true);
      try {
        const documentTypesData = await getDocumentTypes();
        setDocumentTypes(documentTypesData);
      } catch (error) {
        console.error('Failed to load document types:', error);
        toast.error('Failed to load document types');
      } finally {
        setDocumentTypesLoading(false);
      }

      // Load court types
      setCourtTypesLoading(true);
      try {
        const courtTypesData = await getCourtTypes();
        setCourtTypes(courtTypesData);
      } catch (error) {
        console.error('Failed to load court types:', error);
        toast.error('Failed to load court types');
      } finally {
        setCourtTypesLoading(false);
      }

      // Load organizations
      setOrganizationsLoading(true);
      try {
        const organizationsData = await getOrganizations();
        setOrganizations(organizationsData);
      } catch (error) {
        console.error('Failed to load organizations:', error);
        toast.error('Failed to load organizations');
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadData();
  }, []);

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      // This will be handled by individual DocumentPreview components
      console.log('DocumentUpload component unmounting');
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Upload Documents
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Upload new documents to the system. Supported formats: PDF, DOC, DOCX, TXT
        </p>
      </div>

      <div className="space-y-6">
        {/* File Selection and Preview Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* File Upload Area */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Select Files
            </h2>

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              {isDragActive ? (
                <p className="text-blue-600 dark:text-blue-400">Drop the files here...</p>
              ) : (
                <div>
                  <p className="text-gray-600 dark:text-gray-400">
                    Drag and drop files here, or click to select files
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    PDF, DOC, DOCX, TXT files only
                  </p>
                </div>
              )}
            </div>


            {/* Upload Progress */}
            {isUploading && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Uploading...
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {uploadProgress.toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Document Preview Section */}
        {uploadedFiles.length > 0 && (
          <div ref={previewRef}>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Document Preview ({uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''})
            </h2>
            <div className="space-y-4">
              {uploadedFiles.map((file, index) => (
                <DocumentPreview
                  key={index}
                  file={file}
                  onRemove={() => removeFile(index)}
                  onReplace={() => {
                    // Trigger file input for replacement
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf,.doc,.docx,.txt';
                    input.onchange = (e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.files && target.files[0]) {
                        const newFiles = [...uploadedFiles];
                        newFiles[index] = target.files[0];
                        setUploadedFiles(newFiles);
                      }
                    };
                    input.click();
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Document Information Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Document Information
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Type *
              </label>
              <select
                {...register('document_type', { required: 'Document type is required' })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select document type</option>
                {documentTypes.map((type) => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.document_type && (
                <p className="mt-1 text-sm text-red-600">{errors.document_type.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title *
              </label>
              <input
                {...register('title', { required: 'Title is required' })}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Document title (optional)"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Summary
              </label>
              <textarea
                {...register('summary')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Brief summary of the document"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Author
              </label>
              <input
                {...register('author')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Document's author or issuing authority"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Topics
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={currentTopic}
                  onChange={(e) => setCurrentTopic(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTopic();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Type a topic and hit Enter or Add"
                />
                <button
                  type="button"
                  onClick={addTopic}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg font-medium transition-colors duration-200"
                >
                  Add
                </button>
              </div>

              {topics.length > 0 && (
                <div className="mt-3 flex overflow-x-auto whitespace-nowrap space-x-2 pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                  {topics.map((topic, index) => (
                    <span
                      key={index}
                      className="inline-flex flex-shrink-0 items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                    >
                      {topic}
                      <button
                        type="button"
                        onClick={() => removeTopic(topic)}
                        className="ml-2 inline-flex items-center text-blue-400 hover:text-blue-600 dark:hover:text-blue-200 focus:outline-none"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Citation *
                </label>
                <input
                  {...register('citation', { required: 'Citation is required' })}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Legal citation"
                />
                {errors.citation && (
                  <p className="mt-1 text-sm text-red-600">{errors.citation.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Publisher *
                </label>
                <AutoComplete
                  value={publisherValue || ''}
                  onChange={(value) => { setValue('publisher', value); trigger('publisher'); }}
                  placeholder="Publisher name"
                  options={publishers}
                  loading={publishersLoading}
                />

                <input
                  type="hidden"
                  {...register('publisher', {
                    required: 'Publisher is required',
                    minLength: {
                      value: 2,
                      message: 'Publisher name must be at least 2 characters'
                    },
                    maxLength: {
                      value: 200,
                      message: 'Publisher name cannot exceed 200 characters'
                    }
                  })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization *
                </label>
                <AutoComplete
                  value={organizationValue || ''}
                  onChange={(value) => { setValue('organization', value); trigger('organization') }}
                  placeholder="Organization name"
                  options={organizations}
                  loading={organizationsLoading}
                />
                <input
                  type="hidden"
                  {...register('organization', {
                    required: 'Organization is required',
                    minLength: {
                      value: 2,
                      message: 'Organization name must be at least 2 characters'
                    },
                    maxLength: {
                      value: 200,
                      message: 'Organization name cannot exceed 200 characters'
                    }
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Court *
                </label>
                <select
                  {...register('court', { required: 'Court type is required' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select Court</option>
                  {courtTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.court && (
                  <p className="mt-1 text-sm text-red-600">{errors.court.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date of Document *
              </label>
              <input
                {...register('date_of_document',
                  {
                    required: 'Date is required',

                  },
                )}
                type="date"
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              {errors.date_of_document && (
                <p className="mt-1 text-sm text-red-600">{errors.date_of_document.message}</p>
              )}

            </div>

            {/* OCR Option for PDFs */}
            {uploadedFiles.some(file => file.name.toLowerCase().endsWith('.pdf')) && (
              <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="use_ocr"
                    checked={useOCR}
                    onChange={(e) => setUseOCR(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <label htmlFor="use_ocr" className="block text-sm font-medium text-blue-900 dark:text-blue-200">
                      Enable OCR (Optical Character Recognition)
                    </label>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      Use OCR to extract text from scanned PDFs and image-based documents. This may take longer but provides better text extraction for documents with images or poor quality scans.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isUploading || uploadedFiles.length === 0}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Documents
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;
